import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewChecked,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { TransactionRequest } from 'src/app/model/TransactionRequest';
import { TransactionRequestService } from 'src/app/services/transaction-request/transaction-request.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';

@Component({
  selector: 'app-borrower-transaction-requests-table',
  templateUrl: './borrower-transaction-requests-table.component.html',
  styleUrl: './borrower-transaction-requests-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BorrowerTransactionRequestsTableComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  /** Dependency injections */
  private _transactionRequestService = inject(TransactionRequestService);
  private _dialogService = inject(DialogService);

  /** Data source for the table */
  dataSource = new MatTableDataSource<TransactionRequest>([]);

  /** Loading state */
  loading = true;

  /** Table columns - commented out fields come from @product */
  displayedColumns: string[] = [
    //'tx_request_id',
    'created_at',
    'supplier_company',
    'amount',
    'statusProductFormat',
    'actions',
  ];

  /** Subscription for data */
  private transactionRequestsSub?: Subscription;

  /** Sort reference */
  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  private sortBound = false;

  ngOnInit(): void {
    // Sorting accessor so dates, numbers, strings work properly
    this.dataSource.sortingDataAccessor = (item, property) => {
      let value;
      switch (property) {
        case 'created_at':
          value =
            item.created_at instanceof Date
              ? item.created_at.getTime()
              : Date.parse(String(item.created_at)) || 0;
          break;
        case 'amount':
          value = Number(item.amount) || 0;
          break;
        case 'supplier_company':
          value = (item.supplier_company || '').toLowerCase();
          break;
        case 'statusProductFormat':
          value = (item.statusProductFormat || '').toLowerCase();
          break;
        default:
          value = (item as any)[property];
      }
      return value;
    };

    // Subscribe to service changes
    this.transactionRequestsSub =
      this._transactionRequestService.changes$.subscribe(
        (transactionRequests: TransactionRequest[]) => {
          this.dataSource.data = transactionRequests || [];
          this.loading = false;
        },
      );

    // Initial load
    const initial = this._transactionRequestService.getEntities();
    if (initial) {
      this.dataSource.data = initial;
      this.loading = false;
    }
  }

  ngAfterViewChecked(): void {
    if (!this.sortBound && this.sort) {
      this.sortBound = true;
      this.dataSource.sort = this.sort;
      this.sort.active = 'created_at';
      this.sort.direction = 'desc';
      this.sort.sortChange.emit({ active: 'created_at', direction: 'desc' });
      console.log('[Sort] Borrower table sort bound (created_at desc)');
    }
  }

  ngOnDestroy(): void {
    if (this.transactionRequestsSub) {
      this.transactionRequestsSub.unsubscribe();
    }
  }

  openReviewOfferDialog(transactionRequest: TransactionRequest): void {
    // TODO: request/offer flow
    this._dialogService.openReviewOfferDialog(transactionRequest);
    //this._dialogService.openReviewRequestDialog(transactionRequest);
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  formatDate(date: Date | null): string {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(date));
  }
}
